import React, { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useForm, FormProvider } from 'react-hook-form'
import { Helmet } from '@dr.pogodin/react-helmet'
import { useTranslation } from 'react-i18next'
import { Divider, Card as MuiCard, CardContent, Grid, Typography, Button, CircularProgress, Stack } from '@mui/material'
import styled from '@emotion/styled'
import { spacing } from '@mui/system'
import { normalizedWarrantName } from '@finance-lab/utils/shared'

import useAuth from '../../../hooks/useAuth'
import { RegisteredTextField } from '../../../components/fields/TextField'
import { RegisteredCheckBox } from '../../../components/fields/CheckBox'

import ShareSelect from '../../../components/entities/ShareSelect'
import { RegisteredIsinField } from '../../../components/fields/IsinField'
import { WarrantStatusDescription } from '../../../components/entities/WarrantStatus'
import WarrantApi from '../../../api/WarrantApi'
import { EditWarrantForm } from './AdminEditWarrantForm'
import { EnrichedWarrantEntity } from 'types/WarrantEntity'

const Card = styled(MuiCard)(spacing)

function NewWarrantForm() {
    const navigate = useNavigate()
    const formMethods = useForm()
    const [saving, setSaving] = useState(false)
    const warrantApi = new WarrantApi()
    const handleCreateWarrant = async () => {
        if (saving) return
        setSaving(true)
        const data = formMethods.getValues()
        data.shareId = data.share?.id
        delete data.share
        // set all fields to null if empty string
        Object.keys(data).forEach((key) => {
            if (data[key] === '') data[key] = null
        })
        const newWarrant = await warrantApi.create(data)
        navigate(`/admin/warrants/${newWarrant?.id}`)
        setSaving(false)
    }
    return (
        <FormProvider {...formMethods}>
            <Card mb={6}>
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid size={12}>
                            <ShareSelect />
                        </Grid>
                        <Grid size={{ xs: 12, md: 3 }}>
                            <RegisteredTextField name='name' label='Namn' fullWidth />
                        </Grid>
                        <Grid size={{ xs: 12, md: 3 }}>
                            <RegisteredIsinField name='isinWarrant' label='ISIN' fullWidth />
                        </Grid>
                        <Grid size={{ xs: 12, md: 3 }}>
                            <RegisteredCheckBox name='unlisted' label='Onoterad' />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <RegisteredTextField name='internalComment' label='Intern kommentar' fullWidth multiline />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <RegisteredTextField name='externalComment' label='Extern kommentar' fullWidth multiline />
                        </Grid>
                        <Grid size={{ xs: 12, md: 3 }}>
                            <Button
                                variant='contained'
                                color='primary'
                                fullWidth
                                onClick={() => void handleCreateWarrant()}
                                disabled={saving}
                            >
                                Skapa {saving && <CircularProgress size={24} />}
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        </FormProvider>
    )
}

const warrantApi = new WarrantApi()

function WarrantForm() {
    const { warrantId } = useParams()
    const { t } = useTranslation()
    const [warrantLabel, setWarrantLabel] = useState('')
    const { isAuthenticated } = useAuth()
    const [warrant, setWarrant] = useState<(EnrichedWarrantEntity & { statusHideUntil: null }) | null>(null)
    // Note: this is a hack to avoid the entire form being re-rendered when the warrant is updated:
    const [warrantToShowStatus, setWarrantToShowStatus] = useState<
        (EnrichedWarrantEntity & { statusHideUntil: null }) | null
    >(null)
    const fetchData = useCallback(async () => {
        if (!warrantId) return

        const warrant = await warrantApi.findByWarrantId(warrantId)
        if (!warrant) return

        const formattedWarrant = { ...warrant, statusHideUntil: null }
        setWarrant(formattedWarrant)
        setWarrantToShowStatus(formattedWarrant)
        setWarrantLabel(formattedWarrant ? normalizedWarrantName(formattedWarrant) : '-')
    }, [warrantId])

    useEffect(() => {
        if (!isAuthenticated || !warrantId || warrantId === 'new') return
        void fetchData()
    }, [fetchData, isAuthenticated, setWarrantLabel, warrantId])

    const pageTitle =
        warrantId === 'new' ? 'Ny teckningsoption' : t('site.warrants.detailsTitle', { name: warrantLabel })
    return (
        <>
            <Helmet title={pageTitle} />
            <Typography variant='h3' gutterBottom display='block'>
                {pageTitle}
            </Typography>

            <Stack direction='row' spacing={4}>
                <WarrantStatusDescription warrant={warrantToShowStatus} />
                <Typography variant='body1'>
                    <Link to={`/warrants/${warrantId}`}>[Visa detaljvy]</Link>
                </Typography>
                {warrant?.companyId ? (
                    <Typography variant='body1'>
                        <Link to={`/companies/${warrant?.companyId}`}>[Visa bolagsvy]</Link>
                    </Typography>
                ) : null}
            </Stack>

            <Divider sx={{ marginY: 6 }} />
            {warrantId && warrantId !== 'new' && warrant && (
                <EditWarrantForm warrant={warrant} refetchData={fetchData} />
            )}
            {warrantId === 'new' && <NewWarrantForm />}
        </>
    )
}

export default WarrantForm
